import { useEffect, useRef, useState } from 'react';
import { watchStep } from '../utils/videoWatchdog';

const TICK_MS = 1000;

/** Frames an element has shown, counted by requestVideoFrameCallback where the browser has it, else null. */
function frameCounter(): { count: (el: HTMLVideoElement) => number | null } {
  const counts = new WeakMap<HTMLVideoElement, { n: number }>();
  return {
    count: (el) => {
      const v = el as HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number };
      if (typeof v.requestVideoFrameCallback !== 'function') return null;
      let c = counts.get(el);
      if (!c) {
        const box = { n: 0 };
        c = box;
        counts.set(el, box);
        const loop = () => { box.n += 1; v.requestVideoFrameCallback!(loop); };
        v.requestVideoFrameCallback(loop);
      }
      return c.n;
    },
  };
}

/**
 * Keeps the other side's video showing while their camera is on (utils/videoWatchdog): every second the
 * element gets its stream back if it lost it, is played if paused, and is attached afresh after three
 * seconds without a new frame. Returns how many times it had to step in, for the diagnostics overlay.
 */
export function useVideoWatchdog(getEl: () => HTMLVideoElement | null, stream: MediaStream | null, want: boolean): number {
  const [restarts, setRestarts] = useState(0);
  const frames = useRef(frameCounter());
  useEffect(() => {
    if (!want || !stream) return;
    let stalled = 0;
    let last: { el: HTMLVideoElement; frames: number | null; time: number } | null = null;
    const id = setInterval(() => {
      const el = getEl();
      if (!el) return;
      // Hidden, nothing paints: wait for the page to show again, and count from there.
      if (document.visibilityState === 'hidden') { last = null; stalled = 0; return; }
      const nowFrames = frames.current.count(el);
      const same = last?.el === el;
      const advanced = !same ? true
        : nowFrames !== null && last!.frames !== null ? nowFrames > last!.frames : el.currentTime > last!.time;
      last = { el, frames: nowFrames, time: el.currentTime };
      const step = watchStep({ want, attached: el.srcObject === stream, paused: el.paused, advanced, stalledTicks: stalled });
      stalled = step.stalledTicks;
      if (step.action === 'none') return;
      if (step.action === 'attach' || step.action === 'reattach') {
        if (step.action === 'reattach') el.srcObject = null;
        el.srcObject = stream;
        last = null;
      }
      void el.play().catch(() => { /* refused without a gesture: the next tick tries again */ });
      setRestarts((n) => n + 1);
    }, TICK_MS);
    return () => clearInterval(id);
  }, [getEl, stream, want]);
  return restarts;
}
